<template>
    <div>
      <breadcrumb :items="breadcrumbs" />
      <v-container fluid>
  
        <!-- Page Header -->
        <div class="d-flex align-center mb-6">
          <div>
            <h2 class="mb-1">{{ $t('dueDiligence.iyb.iybRegionalScores') }}</h2>
            <div class="text-body-2 text--secondary">
              {{ $t('dueDiligence.iyb.iybRegionalScoresSubtitle') }}
            </div>
          </div>
          <v-spacer />
          <div class="d-flex" style="gap:8px;">
            <v-btn outlined color="primary" :to="{ name: 'importIYBSpreadsheet' }">
              <v-icon small class="mr-1">mdi-file-upload</v-icon>
              {{ $t('dueDiligence.iyb.importSpreadsheet') }}
            </v-btn>
          </div>
        </div>
  
        <!-- Summary Cards -->
        <v-row class="mb-4">
          <v-col cols="12" sm="3">
            <v-card elevation="0" style="border:1px solid #002287; border-radius:10px; background:#f5f7ff;">
              <v-card-text class="pa-4">
                <div class="text-caption" style="color:#002287;">{{ $t('dueDiligence.iyb.totalRegencies') }}</div>
                <div class="text-h5 font-weight-bold" style="color:#002287;">{{ stats.total }}</div>
                <div class="text-caption text--secondary">{{ $t('dueDiligence.iyb.allIndonesiaDistricts') }}</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="3">
            <v-card elevation="0" style="border:1px solid #C1FFE7; border-radius:10px; background:#f8fff8;">
              <v-card-text class="pa-4">
                <div class="text-caption" style="color:#489E16;">{{ $t('dueDiligence.iyb.greenLowRisk') }}</div>
                <div class="text-h5 font-weight-bold" style="color:#489E16;">{{ stats.green }}</div>
                <div class="text-caption text--secondary">{{ $t('dueDiligence.iyb.fastTrackEligible') }}</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="3">
            <v-card elevation="0" style="border:1px solid #FFA826; border-radius:10px; background:#fffdf8;">
              <v-card-text class="pa-4">
                <div class="text-caption" style="color:#FFA826;">{{ $t('dueDiligence.iyb.yellowMediumRisk') }}</div>
                <div class="text-h5 font-weight-bold" style="color:#FFA826;">{{ stats.yellow }}</div>
                <div class="text-caption text--secondary">{{ $t('dueDiligence.iyb.mitigationRequired') }}</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="3">
            <v-card elevation="0" style="border:1px solid #DA3D3D; border-radius:10px; background:#fffafa;">
              <v-card-text class="pa-4">
                <div class="text-caption" style="color:#DA3D3D;">{{ $t('dueDiligence.iyb.redHighRisk') }}</div>
                <div class="text-h5 font-weight-bold" style="color:#DA3D3D;">{{ stats.red }}</div>
                <div class="text-caption text--secondary">{{ $t('dueDiligence.iyb.blockedAppealPath') }}</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
  
        <!-- Main Table -->
        <v-card elevation="0">
          <v-card-text class="px-5 py-4">
  
            <!-- Filters -->
            <div class="d-flex flex-wrap mb-4" style="gap:12px;">
              <v-text-field
                v-model="search"
                prepend-inner-icon="mdi-magnify"
                outlined
                dense
                :placeholder="$t('dueDiligence.iyb.searchRegencyName')"
                class="shrink"
                style="min-width:260px; max-width:300px;"
                @input="searchQuery"
              />
              <v-select
                v-model="filterTier"
                :items="tierOptions"
                item-text="label"
                item-value="value"
                :label="$t('dueDiligence.iyb.filterByTier')"
                outlined
                dense
                clearable
                style="max-width:180px;"
                @change="filterData"
              />
            
              <v-spacer />
              <div class="py-1 title">
                {{ fromCount }} - {{ toCount }} of {{ totalCount }}
              </div>
              <v-btn class="mx-1" fab small outlined color="primary" :disabled="page <= 1" @click="pageChange(false)">
                <v-icon>mdi-chevron-left</v-icon>
              </v-btn>
              <v-btn class="mx-1" fab small outlined color="primary" :disabled="page >= totalPages" @click="pageChange(true)">
                <v-icon>mdi-chevron-right</v-icon>
              </v-btn>
            </div>
  
            <v-data-table
              :headers="headers"
              :items="scores"
              :items-per-page="10"
              hide-default-footer
              :loading="loading"
              :loading-text="$t('loadingData')"
              elevation="0"
              class="exchange-table iyb-score-table"
            >
              <!-- IYB Composite Score -->
              <template v-slot:item.compositeScore="{ item }">
                <div class="iyb-cell-center">
                  <span
                    class="font-weight-bold"
                    :style="'color:' + getScoreColor(item.compositeScore)"
                  >
                    {{ item.compositeScore != null ? item.compositeScore.toFixed(2) : '—' }}
                  </span>
                </div>
              </template>

              <!-- Forest Fire Prevention — Palm Oil gating indicator (IYBv2_BOGOR_Forest_Fire) -->
              <template v-slot:item.firePrevention="{ item }">
                <div class="iyb-cell-center">
                  <span class="font-weight-bold"
                  :style="'color:' + getScoreColor(item.indicators.ind2_forestLandFires)"
                  >
                    {{ item.indicators.ind2_forestLandFires.toFixed(1) }}
                  </span>
                </div>
              </template>

              <!-- EUDR Risk Status -->
              <template v-slot:item.eudrRiskStatus="{ item }">
                <div class="iyb-cell-center">
                  <v-btn
                    small
                    rounded
                    depressed
                    class="iyb-risk-btn"
                    :class="getRiskClass(deriveRiskStatus(item))"
                    min-width="130"
                    height="30"
                  >
                    {{ normalizeRiskStatusLabel(deriveRiskStatus(item)) || 'N/A' }}
                  </v-btn>
                </div>
              </template>

              <!-- Data Year -->
              <template v-slot:item.dataYear="{ item }">
                <div class="iyb-cell-center">
                  <span class="font-weight-medium">{{ item.dataYear }}</span>
                </div>
              </template>

              <!-- Actions -->
              <template v-slot:item.actions="{ item }">
                <div class="iyb-cell-center">
                  <v-btn icon small @click="viewBreakdown(item)">
                    <v-icon small>mdi-eye</v-icon>
                  </v-btn>
                </div>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
  
      </v-container>
  
      <!-- Indicator Breakdown Dialog -->
      <v-dialog v-model="breakdownDialog" max-width="760" scrollable>
        <v-card v-if="selectedRegency">
          <v-card-title class="d-flex align-center">
            <v-icon color="primary" class="mr-2">mdi-chart-bar</v-icon>
            {{ $t('dueDiligence.iyb.indicatorBreakdownTitle') }}
            <v-spacer />
            <v-btn icon @click="breakdownDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider />
          <v-card-text class="pa-6">
            <IYBIndicatorBreakdown
              :regency-name="selectedRegency.regencyName"
              :eudr-risk-status="deriveRiskStatus(selectedRegency)"
              :indicators="selectedRegency.indicators || {}"
              :data-year="selectedRegency.dataYear"
              :imported-at="selectedRegency.importedAt"
            />
          </v-card-text>
        </v-card>
      </v-dialog>
  
    </div>
  </template>
  
  <script>
  import IYBIndicatorBreakdown from './components/IYBIndicatorBreakdown.vue'
  import IYBScoreService from '@/_services/IYBScoreService'
  import {
    getIybRiskScoreColor,
    normalizeRiskStatusLabel,
    classifyIybIndicatorValue,
    deriveIybRiskStatus,
    getIybIndicatorBadgeClass,
    getIybRiskBadgeClass,
  } from '@/utils/iybRiskClassification'
  export default {
    name: 'IYBScoreIndexList',
    components: { IYBIndicatorBreakdown },
    async mounted() {
      await this.loadAvailableYears()
      await this.loadScores()
    },
    data() {
      return {
        loading: false,
        search: '',
        filterTier: null,
        filterYear: null,
        availableYears: [],
        page: 1,
        limit: 10,
        totalCount: 0,
        totalPages: 0,
        fromCount: 0,
        toCount: 0,
        scores: [],
        selectedRegency: null,
        breakdownDialog: false,
        stats: { total: 0, green: 0, yellow: 0, red: 0 },
        tierOptions: [
          { label: this.$t('dueDiligence.iyb.greenLowRisk'), value: 'Low Risk' },
          { label: this.$t('dueDiligence.iyb.yellowMediumRisk'), value: 'Medium Risk' },
          { label: this.$t('dueDiligence.iyb.redHighRisk'), value: 'High Risk' },
        ],
        headers: [
          { text: this.$t('dueDiligence.iyb.regencyId'), value: 'kabProfileId', class: 'black--text', width: '110px', sortable: true },
          { text: this.$t('dueDiligence.iyb.regencyName'), value: 'regencyName', class: 'black--text', sortable: true },
          { text: this.$t('dueDiligence.iyb.iybScore'), value: 'compositeScore', class: 'black--text text-center', align: 'center', sortable: true, width: '120px' },
          { text: this.$t('dueDiligence.iyb.firePrevention'), value: 'firePrevention', class: 'black--text text-center', align: 'center', sortable: false, width: '140px' },
          { text: this.$t('dueDiligence.iyb.eudrRiskStatus'), value: 'eudrRiskStatus', class: 'black--text text-center', align: 'center', sortable: true, width: '160px' },
          { text: this.$t('dueDiligence.iyb.year'), value: 'dataYear', class: 'black--text text-center', align: 'center', sortable: true, width: '90px' },
          { text: this.$t('dueDiligence.iyb.details'), value: 'actions', class: 'black--text text-center', align: 'center', sortable: false, width: '90px' },
        ],
        breadcrumbs: [
          { text: this.$t('deforestation.dueDeligence'), disabled: false, exact: true },
          { text: this.$t('dueDiligence.iyb.iybRegionalScores'), disabled: true },
        ],
      }
    },
    computed: {
      yearOptions() {
        const latest = {
          label: this.$t('dueDiligence.iyb.latestPerRegency'),
          value: null,
        }
        const years = (this.availableYears || []).map(year => ({
          label: String(year),
          value: year,
        }))
        return [latest, ...years]
      },
    },
    methods: {
      async loadAvailableYears() {
        try {
          this.availableYears = await IYBScoreService.getAvailableYears()
          if (this.availableYears.length && this.filterYear == null) {
            this.filterYear = this.availableYears[0]
          }
        } catch (e) {
          console.error(e)
          this.availableYears = []
        }
      },
      async loadScores() {
        this.loading = true
        try {
          const [listRes, statsRes] = await Promise.all([
            IYBScoreService.getAll({
              page: this.page,
              limit: this.limit,
              search: this.search,
              tier: this.filterTier,
              year: this.filterYear,
            }),
            IYBScoreService.getStats(this.filterYear),
          ])
          this.scores = listRes.rows || []
          this.totalCount = listRes.totalCount || 0
          this.stats = statsRes
          this.totalPages = Math.ceil(this.totalCount / this.limit) || 1
          this.fromCount = this.totalCount ? (this.page - 1) * this.limit + 1 : 0
          this.toCount = Math.min(this.page * this.limit, this.totalCount)
        } catch (e) {
          console.error(e)
          this.$notify({ title: this.$t('dueDiligence.iyb.failedToLoadScores'), type: 'error' })
        } finally {
          this.loading = false
        }
      },
      searchQuery() {
        clearTimeout(this.debounceTimer)
        this.debounceTimer = setTimeout(() => {
          this.page = 1
          this.loadScores()
        }, 500)
      },
      filterData() {
        this.page = 1
        this.loadScores()
      },
      pageChange(next) {
        this.page = next ? this.page + 1 : this.page - 1
        this.loadScores()
      },
      viewBreakdown(item) {
        this.selectedRegency = item
        this.breakdownDialog = true
      },
      deriveRiskStatus(item) {
        return deriveIybRiskStatus(item)
      },
      normalizeRiskStatusLabel,
      classifyFirePrev(val) {
        return classifyIybIndicatorValue(val)
      },
      getIndicatorBadgeClass(val) {
        return getIybIndicatorBadgeClass(val)
      },
      getRiskClass(status) {
        return getIybRiskBadgeClass(status)
      },
      getScoreColor(score) {
        return getIybRiskScoreColor(score)
      },
    }
  }
  </script>

  <style scoped>
  .iyb-score-table >>> thead th {
    white-space: nowrap;
  }

  .iyb-score-table >>> thead th.text-center {
    text-align: center !important;
  }

  .iyb-score-table >>> tbody td {
    vertical-align: middle;
  }

  .iyb-cell-center {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
  }

  .iyb-risk-btn {
    text-transform: none;
    letter-spacing: normal;
    font-weight: 600;
    font-size: 12px;
  }

  .iyb-indicator-chip {
    min-width: 76px;
    justify-content: center;
    font-size: 10px;
    font-weight: 600;
  }

  .iyb-badge--green {
    background-color: #e8f5e9 !important;
    border: 2px solid #2e7d32 !important;
    color: #1b5e20 !important;
  }

  .iyb-badge--yellow {
    background-color: #fff8e1 !important;
    border: 2px solid #FFA826 !important;
    color: #FFA826 !important;
  }

  .iyb-badge--red {
    background-color: #ffebee !important;
    border: 2px solid #c62828 !important;
    color: #b71c1c !important;
  }

  .iyb-badge--gray {
    background-color: #f5f5f5 !important;
    border: 2px solid #757575 !important;
    color: #424242 !important;
  }

  .iyb-risk-btn.iyb-badge--green >>> .v-btn__content,
  .iyb-risk-btn.iyb-badge--yellow >>> .v-btn__content,
  .iyb-risk-btn.iyb-badge--red >>> .v-btn__content,
  .iyb-risk-btn.iyb-badge--gray >>> .v-btn__content {
    color: inherit !important;
  }
  </style>